import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import {
  LuCircleCheck,
  LuCircleX,
  LuLock,
  LuPresentation,
  LuUsers,
  LuArrowUpRight,
} from 'react-icons/lu';
import { PhonicsLessonProgress, PhonicsMessage } from '@/api/types';
import { getPasscodeFromUuid } from '@/utils/uuid';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { useData } from '@/context/Data';
import { useLiveLesson } from '@/context/LiveLesson';
import { RoundProgressButton, Button } from '@/components';

type StudentProgressState = 'completed' | 'in-progress' | 'not-started';

const ProgressPip = ({ state }: { state: StudentProgressState }) => (
  <span
    className={clsx(
      'mx-auto block h-3.5 w-3.5 rounded-full',
      state === 'completed' && 'bg-[var(--c-success)]',
      state === 'in-progress' && 'bg-[var(--c-action)]',
      state === 'not-started' && 'bg-pt-ds-line',
    )}
  />
);

interface ClassStatusItemProps {
  label: string;
  value: React.ReactNode;
}

const ClassStatusItem = ({ label, value }: ClassStatusItemProps) => (
  <div className="flex">
    <div className="w-48 text-lg">{label}</div>

    <div>{value}</div>
  </div>
);

interface ClassStatusProps {
  className?: string;
  asPopOver?: boolean;
}

type TabId = 'class' | 'students' | 'popout';

export const ClassStatus = ({
  className,
  asPopOver = true,
}: ClassStatusProps) => {
  const { classStatus, currentLesson, classMessages, clearClassMessage } =
    useData();

  const { scrollToSlideId } = useLessonPlayer();

  const { liveLessonStatus } = useLiveLesson();

  const [lessonIsLive, setLessonIsLive] = useState(false);

  const [showDataPopup, setShowDataPopup] = useState(!asPopOver);

  const [lockScreenMessages, setLockScreenMessages] = useState<
    Record<string, PhonicsMessage>
  >({});

  const [unlockCode, setUnlockCode] = useState<string | null>(null);

  const [currentTabId, setCurrentTabId] = useState<TabId>('students');

  const [hideUnlockCode, setHideUnlockCode] = useState(true);

  const userHasLockScreenOn = useCallback(
    (userId: string) =>
      Object.keys(lockScreenMessages).some(
        (messageKey) => lockScreenMessages[messageKey].fromUserId === userId,
      ),
    [lockScreenMessages],
  );

  const getLockScreenMessageIdByUserId = useCallback(
    (userId: string) =>
      Object.keys(lockScreenMessages).find(
        (messageKey) => lockScreenMessages[messageKey].fromUserId === userId,
      ),
    [lockScreenMessages],
  );

  useEffect(() => {
    if (!liveLessonStatus) {
      return;
    }

    setLessonIsLive(!!liveLessonStatus.startedAt);
    setUnlockCode(
      liveLessonStatus.classId
        ? getPasscodeFromUuid(liveLessonStatus.classId)
        : '',
    );
  }, [liveLessonStatus]);

  useEffect(() => {
    setLockScreenMessages(
      Object.keys(classMessages).reduce((acc, messageKey) => {
        const message = classMessages[messageKey];

        return message.type === 'LOCK_SCREEN'
          ? {
              ...acc,
              [messageKey]: message,
            }
          : {};
      }, {}),
    );
  }, [classMessages]);

  return (
    <div className={className}>
      {asPopOver && (
        <RoundProgressButton
          className={clsx(
            Object.keys(classMessages).length > 0 && 'animate-bounce',
          )}
          color="white"
          onClick={() => setShowDataPopup(!showDataPopup)}
          size="sm"
        />
      )}

      {showDataPopup && (
        <div
          className={clsx(
            'text-pt-ds-ink',
            asPopOver &&
              'absolute bottom-full left-0 -translate-y-3 w-[800px] h-96 overflow-auto rounded-2xl border border-pt-ds-line bg-white shadow-[0_12px_32px_rgba(0,0,0,0.18)]',
          )}
        >
          <div className="sticky top-0 z-10 flex items-center gap-1 border-b border-pt-ds-line bg-white px-3 pt-1">
            <button
              type="button"
              onClick={() => setCurrentTabId('class')}
              className={clsx(
                'inline-flex items-center gap-2 border-b-2 px-4 py-3 font-inter text-sm font-semibold',
                currentTabId === 'class'
                  ? 'border-pt-orange text-pt-orange'
                  : 'border-transparent text-pt-ds-muted',
              )}
            >
              <LuPresentation className="h-5 w-5" /> Class
            </button>

            <button
              type="button"
              onClick={() => setCurrentTabId('students')}
              className={clsx(
                'inline-flex items-center gap-2 border-b-2 px-4 py-3 font-inter text-sm font-semibold',
                currentTabId === 'students'
                  ? 'border-pt-orange text-pt-orange'
                  : 'border-transparent text-pt-ds-muted',
              )}
            >
              <LuUsers className="h-5 w-5" /> Students
            </button>

            {asPopOver && (
              <button
                type="button"
                aria-label="Open in new tab"
                onClick={() =>
                  window.open(`/phonics/status/${currentLesson?.id}`, '_blank')
                }
                className="ml-auto p-3 text-pt-ds-muted hover:text-pt-ds-ink"
              >
                <LuArrowUpRight className="h-5 w-5" />
              </button>
            )}
          </div>

          {currentTabId === 'class' && (
            <div className="flex flex-col gap-2 p-6">
              <ClassStatusItem
                label="Lesson"
                value={`${currentLesson?.number} (week ${currentLesson?.group?.name})`}
              />

              <ClassStatusItem
                label="Class is live"
                value={lessonIsLive ? 'Yes' : 'No'}
              />

              {lessonIsLive && (
                <ClassStatusItem
                  label="Access code"
                  value={liveLessonStatus?.accessCode}
                />
              )}

              {unlockCode && (
                <ClassStatusItem
                  label="Unlock code"
                  value={
                    hideUnlockCode ? (
                      <>
                        {'*'.repeat(unlockCode.length)}{' '}
                        <Button
                          size="sm"
                          onClick={() => setHideUnlockCode(false)}
                        >
                          Show
                        </Button>
                      </>
                    ) : (
                      <>
                        {unlockCode}{' '}
                        <Button
                          size="sm"
                          onClick={() => setHideUnlockCode(true)}
                        >
                          Hide
                        </Button>
                      </>
                    )
                  }
                />
              )}
            </div>
          )}

          {currentTabId === 'students' &&
            lessonIsLive &&
            (() => {
              const learnActivities = (currentLesson?.activities ?? []).filter(
                (activity) => activity.type.mode === 'LEARN',
              );

              const progressState = (
                studentId: string,
                activityId: string,
              ): StudentProgressState => {
                const progress = classStatus?.studentStatuses
                  ?.find(
                    (studentStatus) => studentStatus.studentId === studentId,
                  )
                  ?.lessonStatuses.find(
                    (lessonStatus) =>
                      lessonStatus.lessonId === currentLesson?.id,
                  )
                  ?.activityStatuses.find(
                    (activityStatus) =>
                      activityStatus.activityId === activityId,
                  )?.progress;

                if (progress === PhonicsLessonProgress.COMPLETED) {
                  return 'completed';
                }

                if (progress === PhonicsLessonProgress.IN_PROGRESS) {
                  return 'in-progress';
                }

                return 'not-started';
              };

              return (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left font-inter text-xs font-bold uppercase tracking-wide text-pt-ds-muted">
                          Name
                        </th>
                        <th className="px-4 py-3 text-center font-inter text-xs font-bold uppercase tracking-wide text-pt-ds-muted">
                          Is online
                        </th>
                        {learnActivities.map((activity) => (
                          <th
                            key={activity.id}
                            className="px-4 py-3 text-center font-inter text-xs font-bold uppercase tracking-wide text-pt-ds-muted"
                          >
                            {activity.type.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(liveLessonStatus?.studentLiveStatuses ?? []).map(
                        (row) => (
                          <tr key={row.id} className="hover:bg-pt-cream">
                            <td className="border-t border-pt-ds-line-soft px-4 py-3 font-inter text-sm">
                              {userHasLockScreenOn(row.id) ? (
                                <span className="inline-flex items-center gap-2 font-semibold text-pt-coral-red">
                                  <LuLock className="h-5 w-5" />
                                  {row.name}
                                  <button
                                    type="button"
                                    className="underline"
                                    onClick={() => {
                                      const lockScreenMessageId =
                                        getLockScreenMessageIdByUserId(row.id);

                                      if (lockScreenMessageId) {
                                        clearClassMessage(lockScreenMessageId);
                                      }
                                    }}
                                  >
                                    clear
                                  </button>
                                </span>
                              ) : (
                                <span className="font-semibold text-pt-ds-ink">
                                  {row.name}
                                </span>
                              )}
                            </td>
                            <td className="border-t border-pt-ds-line-soft px-4 py-3 text-center">
                              {row.isOnline ? (
                                <LuCircleCheck className="mx-auto h-6 w-6 text-[var(--c-success)]" />
                              ) : (
                                <LuCircleX className="mx-auto h-6 w-6 text-pt-coral-red" />
                              )}
                            </td>
                            {learnActivities.map((activity) => (
                              <td
                                key={activity.id}
                                className="border-t border-pt-ds-line-soft px-4 py-3"
                              >
                                <ProgressPip
                                  state={progressState(row.id, activity.id)}
                                />
                              </td>
                            ))}
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}

          {currentTabId === 'students' && !lessonIsLive && (
            <div className="flex h-full flex-col items-center justify-center gap-8 p-8 text-center">
              <p className="max-w-md font-inter text-sm text-pt-ds-muted">
                This lesson is not currently live. You can make it live when you
                reach the end of the teaching activities and begin the Learning
                activities.
              </p>

              {asPopOver && (
                <Button onClick={() => scrollToSlideId?.('learning-launcher')}>
                  Skip to the learning launcher
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
